import { Chip, Divider } from "@material-ui/core";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { identity, sortBy } from "lodash";
import {
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { StepperContext } from "../../../../../components/stepper/context";
import { H1 } from "../../../../../components/typography";
import { countries } from "../../../../../store/common/models";
import { LaunchConfigurationsDTO } from "../../../../../store/launch-configurations/models/dto";
import { colours } from "../../../../../styles/colours";
import { Container } from "../../components";
import { ConfigContext } from "../../config-context";
import WorldMap from "./world-map";

const Title = styled(H1)`
  text-align: center;
`;

const StyledDivider = styled(Divider)`
  margin: 3em;
`;

export interface IFirstPageProps {
  item: LaunchConfigurationsDTO.Responses.GetSingle.T;
}

const StyledAccordionDetails = styled(AccordionDetails)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  & > * {
    margin: 0.5em;
  }
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 1em;
  box-shadow: none;
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  font-size: 0.8em;
`;

const ErrorText = styled.div`
  color: ${colours.red};
  font-weight: 500;
`;

const FirstPage: FunctionComponent<IFirstPageProps> = ({ item }) => {
  const sortedList = useMemo(
    () => sortBy(item.configuration.prohibitedCountries, identity),
    [item.configuration.prohibitedCountries]
  );
  const { setIsNextEnabled, setValidation } = useContext(StepperContext);
  const {
    state: { fingerprint },
  } = useContext(ConfigContext);

  const [denied, setDenied] = useState<boolean>(false);

  const { t, i18n } = useTranslation("token-onboarding");
  const currentLanguage = i18n.language.replaceAll("-", "_");

  useEffect(() => {
    if (fingerprint) {
      const countryCode = fingerprint.ipLocation?.country?.code;
      const ipAddress = fingerprint.ip;

      if (
        item.configuration.whitelistedIPs?.find((ip) => ip === ipAddress) ||
        !item.configuration.prohibitedCountries?.find(
          (country) => country === countryCode
        )
      ) {
        setIsNextEnabled(true);
      } else {
        setDenied(true);
        setIsNextEnabled(false);
      }
    } else {
      setIsNextEnabled(false);
    }
  }, [setIsNextEnabled, fingerprint, item.configuration, setDenied]);

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () => Promise.resolve());
  }, [setValidation]);

  return (
    <Container>
      <Title>{item.configuration.launchName?.[currentLanguage]}</Title>
      <StyledDivider />
      <p
        dangerouslySetInnerHTML={{
          __html: item.configuration.welcomeMessage?.[currentLanguage] || '',
        }}
      ></p>
      {item.configuration.prohibitedCountries &&
        item.configuration.prohibitedCountries.length > 0 ? (
        <div>
          <h3>{t("prohibited-countries")}</h3>
          <WorldMap />
          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t("country-list")}</Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              {sortedList.map((c) => (
                <Chip key={c} label={countries.enum[c as typeof countries.T]} />
              ))}
            </StyledAccordionDetails>
          </StyledAccordion>
          {denied ? (
            <ErrorText
              dangerouslySetInnerHTML={{
                __html: t("accessing-from-restricted-country"),
              }}
            />
          ) : null}
        </div>
      ) : null}
    </Container>
  );
};

export const useFirstPage = () => {
  const { t } = useTranslation("page-titles");

  return {
    title: t("welcome"),
    content: FirstPage,
  };
};
